/**
 * One clock, two places on the page.
 *
 * Chapter 2's address bar starts counting the moment the sequence lands in this
 * browser tab. Chapter 1's proof chip, one screen ABOVE it, shows that count
 * from the instant it exists. The arrival plate that used to read it a third
 * time left with its chapter on 22 Aug 2026. It
 * is the strongest honesty device on the page and it only works if it is
 * literally one number: a second component counting from its own mount would be
 * a second animation of the same idea, and it would disagree with the first one
 * by however long the reader took to scroll.
 *
 * The chip being above the thing that starts it is the reason `seconds` is
 * nullable rather than zero. For the first five seconds of a visit the fold has
 * no number to show, and it shows no number.
 *
 * So the timestamp is lifted here and both readers subscribe to it. The
 * provider owns exactly one interval and it only runs once the hero has
 * actually gone live, so a visitor who never reaches the live state pays
 * nothing.
 *
 * NEVER A FABRICATED TIMESTAMP. start() is called by the address bar at the
 * instant the address answers, and by nothing else. This is the one number on
 * the page that has to be true.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { t } from '../../lib/i18n/t';

interface LiveClock {
  /** Seconds since the hero went live, or null while it has not. */
  seconds: number | null;
  /** Called once, by the address bar, at the instant the address answers. */
  start: () => void;
}

const LiveSinceContext = createContext<LiveClock>({ seconds: null, start: () => {} });

export function LiveSinceProvider({ children }: { children: React.ReactNode }) {
  const since = useRef<number | null>(null);
  const [seconds, setSeconds] = useState<number | null>(null);

  const start = useCallback(() => {
    if (since.current !== null) return;
    since.current = Date.now();
    setSeconds(0);
  }, []);

  // One interval for the whole page, and none at all before the hero lands.
  useEffect(() => {
    if (seconds === null) return;
    const id = window.setInterval(() => {
      const from = since.current;
      if (from !== null) setSeconds(Math.floor((Date.now() - from) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [seconds === null]);

  const value = useMemo(() => ({ seconds, start }), [seconds, start]);
  return <LiveSinceContext.Provider value={value}>{children}</LiveSinceContext.Provider>;
}

export function useLiveSince(): LiveClock {
  return useContext(LiveSinceContext);
}

/**
 * The count, in words.
 *
 * One second gets its own key. The counter passes through 1 on the way up, in
 * front of every visitor, and "deployed 1 seconds ago" on the one number this
 * page asks to be believed is not a line to ship. One minute gets its own key
 * for the same reason, in case a long sitting carries the counter into minutes.
 */
export function elapsedLine(seconds: number): string {
  if (seconds === 1) return t('deployed a second ago');
  if (seconds < 90) return t('deployed {n} seconds ago', { n: String(seconds) });
  const minutes = Math.round(seconds / 60);
  if (minutes === 1) return t('deployed a minute ago');
  return t('deployed {n} minutes ago', { n: String(minutes) });
}
